import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import LandingHeader from "@/components/feature/LandingHeader";
import LandingFooter from "@/components/feature/LandingFooter";
import FaqAccordion, { type FaqItem } from "@/components/feature/FaqAccordion";
import LakeFurnasExperience from "@/components/feature/LakeFurnasExperience";
import ServiceReviews from "@/components/feature/ServiceReviews";
import HomeTourPrices from "@/pages/home/components/Services";
import HomeAttractions from "@/pages/home/components/Attractions";
import HomeFleet from "@/pages/home/components/Fleet";
import { PHONE_TEL, WHATSAPP_LINK } from "@/pages/home/constants";
import { useReveal } from "@/hooks/useReveal";

export interface ServiceHighlight {
  icon: string;
  title: string;
  description: string;
}

export interface ServiceStep {
  icon: string;
  title: string;
  description: string;
}

export interface ServiceGalleryItem {
  image: string;
  alt: string;
  caption: string;
}

export interface ServiceRelated {
  to: string;
  title: string;
  description: string;
  icon: string;
}

export interface ServiceLandingProps {
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonical: string;
    jsonLd: unknown;
  };
  breadcrumbLabel: string;
  tagline: string;
  h1: string;
  intro: string;
  heroImage: string;
  heroAlt: string;
  /** "immersive" = full-bleed photo background (default) | "split" = photo framed beside the text | "stacked" = photo banner on top, copy right below. */
  heroVariant?: "immersive" | "split" | "stacked";
  /** Optional Tailwind object-position classes for the immersive hero photo. */
  heroImagePosition?: string;
  /**
   * Mobile: shows the hero photo as a wide banner (whole subject visible, no copy over it)
   * and keeps the essential copy below. Desktop keeps the immersive photo background.
   */
  heroMobileBanner?: boolean;
  priceNote: string;
  highlights: ServiceHighlight[];
  featuresTitle: string;
  featuresSubtitle: string;
  features: ServiceHighlight[];
  stepsTitle: string;
  stepsSubtitle: string;
  steps: ServiceStep[];
  galleryTitle: string;
  gallerySubtitle: string;
  gallery: ServiceGalleryItem[];
  /** Optional Tailwind aspect class for gallery frames, e.g. "aspect-[4/5]" for portrait photos. */
  galleryImageAspect?: string;
  /** "carousel" (default) = horizontal swipe on mobile | "grid" = every photo visible on mobile and desktop. */
  galleryVariant?: "carousel" | "grid";
  /** Desktop column count when galleryVariant is "grid" (mobile always 2 columns). */
  galleryColumns?: 2 | 3 | 4;
  /** Hide the gallery when the same photos already appear in the itinerary/fleet blocks. */
  showGallery?: boolean;
  /** Adds the home-page blocks: Lago de Furnas experience, tour prices, itineraries and fleet. */
  furnasSections?: boolean;
  faqs: FaqItem[];
  /** Optional line shown under the Google reviews heading. */
  reviewsSubtitle?: string;
  ctaTitle: string;
  ctaText: string;
  related: ServiceRelated[];
  /** Optional label for an extra link shown under the related cards (usually pointing to the home page). */
  relatedCtaLabel?: string;
}

export default function ServiceLanding(props: ServiceLandingProps) {
  useReveal();

  const {
    seo,
    breadcrumbLabel,
    tagline,
    h1,
    intro,
    heroImage,
    heroAlt,
    heroVariant = "immersive",
    heroImagePosition = "object-center",
    heroMobileBanner = false,
    priceNote,
    highlights,
    featuresTitle,
    featuresSubtitle,
    features,
    stepsTitle,
    stepsSubtitle,
    steps,
    galleryTitle,
    gallerySubtitle,
    gallery,
    galleryImageAspect = "aspect-[4/3]",
    galleryVariant = "carousel",
    galleryColumns = 3,
    showGallery = true,
    furnasSections = false,
    faqs,
    reviewsSubtitle,
    ctaTitle,
    ctaText,
    related,
    relatedCtaLabel,
  } = props;

  const heroCopy = (
    <>
      <nav className="flex flex-wrap items-center gap-2 text-[11px] md:text-xs text-white/70 mb-4" aria-label="Trilha">
        <Link to="/" className="hover:text-white transition-colors cursor-pointer">
          Início
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-white/90">{breadcrumbLabel}</span>
      </nav>

      <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-[11px] md:text-xs font-semibold text-white/90 mb-4">
        <i className="ri-map-pin-2-fill text-accent-400" />
        Capitólio · Lago de Furnas · Minas Gerais
      </span>

      <p className="font-heading text-[11px] md:text-sm font-semibold tracking-[0.3em] text-accent-400 uppercase mb-3">
        {tagline}
      </p>

      <h1 className="text-2xl md:text-4xl lg:text-[2.75rem] font-heading font-bold text-white leading-[1.15] mb-4 tracking-tight">
        {h1}
      </h1>

      <p className="text-sm md:text-base text-white/80 leading-relaxed mb-5 max-w-xl">{intro}</p>

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={WHATSAPP_LINK}
          data-whatsapp-cta
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-accent-500 text-primary-950 hover:bg-accent-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-lg" />
          Reservar pelo WhatsApp
        </a>
        <span className="inline-flex items-center gap-2 px-4 py-3 rounded-md text-xs md:text-sm font-semibold text-white/90 border border-white/25">
          <i className="ri-price-tag-3-line text-accent-400" />
          {priceNote}
        </span>
      </div>
    </>
  );

  const renderGalleryCard = (item: ServiceGalleryItem, i: number, isGrid: boolean) => (
    <article
      key={item.caption}
      className={`reveal reveal-delay-${(i % 4) + 1} group ${
        isGrid ? "w-full" : "w-[58%] md:w-auto flex-shrink-0 snap-start"
      } bg-background-50 rounded-lg md:rounded-xl overflow-hidden border border-background-200/70 hover:border-accent-500/60 transition-all duration-300`}
    >
      <div className={`relative ${galleryImageAspect} overflow-hidden`}>
        <img
          src={item.image}
          alt={item.alt}
          title={`${item.caption} | Eco Tour Turismo`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-2.5 md:p-4">
        <h3 className="text-[11px] md:text-base font-heading font-bold text-primary-900 leading-tight">
          {item.caption}
        </h3>
      </div>
    </article>
  );

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={seo.canonical}
        jsonLd={seo.jsonLd}
      />
      <LandingHeader />

      {/* HERO */}
      {heroVariant === "stacked" ? (
        <section className="relative w-full bg-background-50">
          <div className="relative w-full h-[190px] md:h-[430px] lg:h-[500px] overflow-hidden bg-primary-950">
            <img
              src={heroImage}
              alt={heroAlt}
              title={heroAlt}
              fetchPriority="high"
              decoding="async"
              className={`w-full h-full object-cover ${heroImagePosition}`}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-10 md:h-24 bg-gradient-to-t from-background-50/90 to-transparent" />
          </div>

          <div className="w-full px-4 md:px-6 lg:px-8 pt-3 md:pt-6 pb-4 md:pb-9">
            <div className="max-w-[1500px] mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 lg:gap-12">
              <div className="min-w-0 max-w-3xl reveal">
                <nav
                  className="flex flex-wrap items-center gap-2 text-[11px] md:text-xs text-foreground-500 mb-2"
                  aria-label="Trilha"
                >
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                    Início
                  </Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-foreground-800">{breadcrumbLabel}</span>
                </nav>

                <p className="font-heading text-[10px] md:text-xs font-semibold tracking-[0.22em] md:tracking-[0.26em] text-accent-700 uppercase mb-1.5">
                  {tagline}
                </p>

                <h1 className="text-[1.5rem] md:text-4xl lg:text-[2.6rem] font-heading font-bold text-primary-900 leading-[1.12] tracking-tight mb-2">
                  {h1}
                </h1>

                <p className="text-[13px] md:text-base text-foreground-600 leading-relaxed max-w-2xl">{intro}</p>
              </div>

              <div className="w-full lg:w-auto flex flex-col sm:flex-row sm:flex-wrap lg:flex-col items-stretch sm:items-center lg:items-end gap-2 flex-shrink-0 reveal">
                <a
                  href={WHATSAPP_LINK}
                  data-whatsapp-cta
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-accent-500 text-primary-950 hover:bg-accent-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Reservar pelo WhatsApp
                </a>
                <span className="inline-flex items-center justify-center gap-2 px-3 py-1.5 md:px-4 md:py-2.5 rounded-md text-[11px] md:text-sm font-semibold text-foreground-700 border border-background-300 bg-background-50">
                  <i className="ri-price-tag-3-line text-accent-600" />
                  {priceNote}
                </span>
                <span className="hidden md:inline-flex items-center justify-center gap-2 text-[11px] md:text-xs font-medium text-foreground-500">
                  <i className="ri-map-pin-2-line text-accent-600" />
                  Capitólio · Lago de Furnas · Minas Gerais
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : heroVariant === "split" ? (
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-950 to-primary-900" />
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-accent-500/10 blur-3xl" />
          <div className="relative w-full px-4 md:px-6 lg:px-8 pt-24 md:pt-32 pb-10 md:pb-14">
            <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-8 lg:gap-14 items-center">
              <div className="min-w-0">{heroCopy}</div>
              <div className="relative w-full max-w-[360px] lg:max-w-none mx-auto lg:mx-0">
                <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden border border-white/15">
                  <img
                    src={heroImage}
                    alt={heroAlt}
                    title={heroAlt}
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover object-[50%_58%]"
                  />
                </div>
                <div className="absolute left-3 right-3 bottom-3 bg-primary-950/85 backdrop-blur-sm border border-white/15 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-speed-up-line text-primary-950 text-base" />
                  </span>
                  <p className="text-[11px] md:text-xs font-semibold text-white leading-snug">
                    Fotos reais de clientes no Lago de Furnas
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : heroMobileBanner ? (
        <section className="relative w-full overflow-hidden bg-primary-950">
          {/* Photo as background on every screen size */}
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={heroAlt}
              title={heroAlt}
              fetchPriority="high"
              decoding="async"
              className={`w-full h-full object-cover ${heroImagePosition}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/10 md:from-primary-950 md:via-primary-950/70 md:to-primary-950/25 lg:bg-gradient-to-r lg:from-primary-950/95 lg:via-primary-950/80 lg:to-primary-950/15" />
            <div className="absolute inset-0 hidden md:block bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/35" />
          </div>

          {/* Compact hero: the copy now sits over the photo on mobile too */}
          <div className="relative flex w-full flex-col justify-end px-4 pb-5 md:min-h-[640px] md:justify-center md:px-6 md:pb-16 md:pt-28 lg:px-8">
            <div className="h-[124px] md:hidden" aria-hidden="true" />
            <div className="max-w-3xl">
              <nav
                className="hidden md:flex flex-wrap items-center gap-2 text-xs text-white/70 mb-4"
                aria-label="Trilha"
              >
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                  Início
                </Link>
                <i className="ri-arrow-right-s-line" />
                <span className="text-white/90">{breadcrumbLabel}</span>
              </nav>

              <div className="flex flex-wrap items-center gap-2 mb-2 md:mb-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 md:px-3.5 md:py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-[10px] md:text-xs font-semibold text-white/90">
                  <i className="ri-map-pin-2-fill text-accent-400" />
                  Capitólio · Lago de Furnas
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 md:hidden rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-[10px] font-semibold text-white/90">
                  <i className="ri-price-tag-3-line text-accent-400" />
                  {priceNote}
                </span>
              </div>

              <p className="hidden md:block font-heading text-sm font-semibold tracking-[0.3em] text-accent-400 uppercase mb-3">
                {tagline}
              </p>

              <h1 className="text-[1.2rem] md:text-4xl lg:text-[2.75rem] font-heading font-bold text-white leading-[1.15] mb-2 md:mb-4 tracking-tight">
                {h1}
              </h1>

              <p className="hidden md:block text-base text-white/80 leading-relaxed mb-5 max-w-xl">
                {intro}
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <a
                  href={WHATSAPP_LINK}
                  data-whatsapp-cta
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 md:px-6 md:py-3 rounded-md text-sm font-bold bg-accent-500 text-primary-950 hover:bg-accent-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Reservar pelo WhatsApp
                </a>
                <span className="hidden md:inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-white/90 border border-white/25">
                  <i className="ri-price-tag-3-line text-accent-400" />
                  {priceNote}
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={heroAlt}
              title={heroAlt}
              fetchPriority="high"
              decoding="async"
              className={`w-full h-full object-cover ${heroImagePosition}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/25 lg:bg-gradient-to-r lg:from-primary-950/95 lg:via-primary-950/80 lg:to-primary-950/15" />
            <div className="absolute inset-0 hidden lg:block bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/35" />
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-8 pt-28 md:pt-36 pb-12 md:pb-16 min-h-[600px] md:min-h-[640px] flex flex-col justify-end lg:justify-center">
            <div className="max-w-3xl">{heroCopy}</div>
            <div className="hidden lg:flex items-center gap-2.5 mt-8 self-start px-3.5 py-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm">
              <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-speed-up-line text-primary-950 text-base" />
              </span>
              <p className="text-[11px] md:text-xs font-semibold text-white leading-snug">
                Fotos reais do passeio de jet ski no Lago de Furnas
              </p>
            </div>
          </div>
        </section>
      )}

      {/* HIGHLIGHTS */}
      <section className="relative bg-background-100 border-y border-background-200/60 py-4 md:py-8">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-6">
            {highlights.map((item, i) => (
              <div
                key={item.title}
                className={`reveal reveal-delay-${(i % 4) + 1} flex items-center gap-2.5 md:gap-3 text-left`}
              >
                <span className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-primary-600 text-base md:text-xl`} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm font-bold text-foreground-900 leading-tight">{item.title}</p>
                  <p className="text-[10px] md:text-xs text-foreground-500 leading-tight md:leading-snug">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {furnasSections && (
        <>
          {/* LAKE EXPERIENCE */}
          <LakeFurnasExperience />

          {/* TOUR PRICES (same blocks as home) */}
          <div className="border-t border-background-200/60">
            <HomeTourPrices />
          </div>
        </>
      )}

      {/* FEATURES */}
      <section className="relative py-6 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1500px] mx-auto">
            <div className="text-center mb-4 md:mb-9 reveal">
              <h2 className="text-lg md:text-3xl font-heading font-bold text-primary-900 mb-1.5 md:mb-3">
                {featuresTitle}
              </h2>
              <p className="text-xs md:text-sm text-foreground-600 max-w-xl mx-auto">{featuresSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 md:gap-6">
              {features.map((item, i) => (
                <div
                  key={item.title}
                  className={`reveal reveal-delay-${(i % 4) + 1} bg-background-50 rounded-lg md:rounded-xl border border-background-200/70 p-3 md:p-6 flex items-start md:block gap-3`}
                >
                  <span className="w-9 h-9 md:w-12 md:h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mb-0 md:mb-4">
                    <i className={`${item.icon} text-accent-700 text-base md:text-xl`} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[13px] md:text-base font-heading font-bold text-primary-900 mb-1 md:mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-[11px] md:text-sm text-foreground-600 leading-relaxed md:leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-4 md:mt-8 reveal">
              <a
                href={WHATSAPP_LINK}
                data-whatsapp-cta
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Reservar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="relative py-6 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1500px] mx-auto">
            <div className="text-center mb-4 md:mb-9 reveal">
              <span className="inline-block text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] text-accent-700 mb-1.5 md:mb-3">
                Como funciona
              </span>
              <h2 className="text-lg md:text-3xl font-heading font-bold text-primary-900 mb-1.5 md:mb-3">{stepsTitle}</h2>
              <p className="text-xs md:text-sm text-foreground-600 max-w-xl mx-auto">{stepsSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 md:gap-6">
              {steps.map((step, i) => (
                <div
                  key={step.title}
                  className={`reveal reveal-delay-${(i % 4) + 1} relative bg-background-50 rounded-lg md:rounded-xl border border-background-200/70 p-3 md:p-6 flex items-start md:block gap-3`}
                >
                  <span className="relative w-9 h-9 md:w-11 md:h-11 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0 mb-0 md:mb-4">
                    <i className={`${step.icon} text-secondary-900 text-base md:text-xl`} />
                    <span className="absolute -top-1 -left-1 w-4 h-4 md:hidden rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[13px] md:text-base font-heading font-bold text-primary-900 mb-1 md:mb-1.5">
                      {step.title}
                    </h3>
                    <p className="text-[11px] md:text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {furnasSections && (
        <>
          {/* ITINERARY / ROTEIROS */}
          <HomeAttractions />

          {/* FLEET / NOSSAS EMBARCAÇÕES */}
          <HomeFleet />
        </>
      )}

      {/* MID CTA */}
      <section className="relative hidden md:block py-8 md:py-14 bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1100px] mx-auto text-center reveal">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-[11px] md:text-xs font-semibold text-white/90 mb-4">
              <i className="ri-customer-service-2-line text-accent-400" />
              Atendimento todos os dias
            </span>
            <h2 className="text-xl md:text-3xl font-heading font-bold text-white mb-3">
              Fale agora com a Eco Tour Turismo
            </h2>
            <p className="text-xs md:text-sm text-white/70 max-w-xl mx-auto mb-6">
              Tire suas dúvidas, consulte valores e garanta a sua reserva no Lago de Furnas — atendimento rápido e personalizado.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={WHATSAPP_LINK}
                data-whatsapp-cta
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-accent-500 text-primary-950 hover:bg-accent-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Reservar pelo WhatsApp
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold text-white border border-white/30 hover:bg-white/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg" />
                Ligar agora
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {showGallery && (
        <section className="relative py-6 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-[1500px] mx-auto">
              <div className="text-center mb-4 md:mb-9 reveal">
                <h2 className="text-lg md:text-3xl font-heading font-bold text-primary-900 mb-1.5 md:mb-3">{galleryTitle}</h2>
                <p className="text-xs md:text-sm text-foreground-600 max-w-xl mx-auto">{gallerySubtitle}</p>
              </div>
              {galleryVariant === "grid" ? (
                <div
                  className={`grid grid-cols-2 gap-3 md:gap-6 ${
                    galleryColumns === 4
                      ? "lg:grid-cols-4"
                      : galleryColumns === 2
                        ? "md:grid-cols-2"
                        : "md:grid-cols-3"
                  }`}
                >
                  {gallery.map((item, i) => renderGalleryCard(item, i, true))}
                </div>
              ) : (
                <>
                  <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 pb-1 snap-x snap-mandatory no-scrollbar">
                    {gallery.map((item, i) => renderGalleryCard(item, i, false))}
                  </div>
                  <p className="md:hidden text-center text-[10px] text-foreground-500 mt-2 mb-0">
                    <i className="ri-drag-move-2-line align-middle mr-1" />
                    Deslize para ver mais fotos
                  </p>
                </>
              )}
              <div className="text-center mt-4 md:mt-8 reveal">
                <a
                  href={WHATSAPP_LINK}
                  data-whatsapp-cta
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Quero reservar o meu passeio
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      <ServiceReviews subtitle={reviewsSubtitle} />

      {/* FAQ */}
      <section className="relative py-6 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1100px] mx-auto">
            <div className="text-center mb-4 md:mb-9 reveal">
              <span className="inline-block text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] text-accent-700 mb-1.5 md:mb-3">
                Dúvidas frequentes
              </span>
              <h2 className="text-lg md:text-3xl font-heading font-bold text-primary-900 mb-1.5 md:mb-3">
                Perguntas Frequentes
              </h2>
            </div>
            <div className="reveal">
              <FaqAccordion items={faqs} />
            </div>
            <div className="text-center mt-4 md:mt-6 reveal">
              <a
                href={WHATSAPP_LINK}
                data-whatsapp-cta
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-accent-500 text-primary-950 hover:bg-accent-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Tirar dúvidas no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-6 md:py-12 bg-accent-500">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1500px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3 md:gap-8 text-center md:text-left reveal">
            <div>
              <h2 className="text-lg md:text-3xl font-heading font-bold text-primary-950 mb-1.5 md:mb-2 leading-tight">
                {ctaTitle}
              </h2>
              <p className="text-xs md:text-sm text-primary-900/80 max-w-md">{ctaText}</p>
            </div>
            <a
              href={WHATSAPP_LINK}
              data-whatsapp-cta
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold bg-primary-950 text-white hover:bg-primary-900 transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-whatsapp-line text-lg" />
              Reservar agora
            </a>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="relative py-6 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-4 md:mb-8 reveal">
              <h2 className="text-lg md:text-2xl font-heading font-bold text-primary-900 mb-1.5 md:mb-2">
                Outras experiências em Capitólio
              </h2>
              <p className="text-xs md:text-sm text-foreground-600">
                Descubra tudo o que a Eco Tour Turismo oferece no Lago de Furnas.
              </p>
            </div>
            <div
              className={`grid grid-cols-1 ${
                related.length > 1 ? "md:grid-cols-2" : "max-w-2xl mx-auto"
              } gap-2.5 md:gap-6`}
            >
              {related.map((item, i) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`reveal reveal-delay-${(i % 4) + 1} group flex items-start gap-3 md:gap-4 bg-background-50 rounded-lg md:rounded-xl border border-background-200/70 hover:border-accent-500/60 p-3 md:p-5 transition-all duration-300 cursor-pointer`}
                >
                  <span className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-600 text-base md:text-xl`} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[13px] md:text-base font-heading font-bold text-primary-900 mb-0.5 md:mb-1 group-hover:text-accent-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-[11px] md:text-sm text-foreground-600 leading-snug md:leading-relaxed">{item.description}</p>
                  </div>
                </Link>
              ))}
            </div>

            {relatedCtaLabel && (
              <div className="text-center mt-4 md:mt-6 reveal">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold text-primary-700 bg-background-50 border border-primary-200 hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {relatedCtaLabel}
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}